import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, createElementVNode as _createElementVNode, vModelSelect as _vModelSelect, withDirectives as _withDirectives, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, withModifiers as _withModifiers, createBlock as _createBlock, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { key: 0 }
const _hoisted_2 = { key: 1 }
const _hoisted_3 = { key: 2 }
const _hoisted_4 = {
  key: 1,
  variant: "danger",
  class: "mt-3"
}

import { ref } from 'vue';
import { useClipboard } from '@vueuse/core';
import { useMutation } from '@vue/apollo-composable';
import { UserApiKeyCreateDocument } from '@/gql/graphql';
import { genApiKey } from '@/utils/api-utils';
import {
  BAlert,
  BButton,
  BInputGroup,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BSpinner,
  BTooltip,
} from 'bootstrap-vue-next';

// --- Emit "apiKeyCreated" when new access token created ---

export default /*@__PURE__*/_defineComponent({
  __name: 'UserNewApiKeyForm',
  emits: ['apiKeyCreated', 'apiKeyDismissed'],
  setup(__props, { emit: __emit }) {

const emits = __emit;

// --- Local state for new API token creation ---
const newTokenName = ref<string>('');
const newTokenValue = ref<string>('');
const newTokenDescription = ref<string>('');
const newTokenPermissions = ref<string>('read');
const newTokenExpiration = ref<string | null>(null);

const creatingToken = ref(false);
const createTokenError = ref<string | null>(null);
const createTokenSuccess = ref(false);
const createTokenShowSuccessIndicator = ref(false);

const createdTokenName = ref<string | null>(null);
const createdTokenValue = ref<string | null>(null);

const showCopyTokenTooltip = ref(false);

// --- Mutation to create a new API token ---
const {
  mutate: createApiKey,
  onDone: onCreateTokenDone,
  onError: onCreateTokenError,
} = useMutation(UserApiKeyCreateDocument);

// When the create mutation returns, show a success message and refetch tokens.
onCreateTokenDone((res) => {
  if (res.data && res.data.userApiKeyCreate) {
    createTokenError.value = null;
    createTokenShowSuccessIndicator.value = true;
    createTokenSuccess.value = true;

    createdTokenName.value = newTokenName.value;
    createdTokenValue.value = newTokenValue.value;

    setTimeout(() => {
      createTokenShowSuccessIndicator.value = false;
    }, 2000);

    newTokenName.value = '';
    newTokenValue.value = '';
    newTokenDescription.value = '';
    newTokenExpiration.value = null;

    emits('apiKeyCreated'); // Emit event to parent component to refetch access tokens
  } else {
    console.error('Token creation failed, missing token data:', res.data);
    createTokenSuccess.value = false;
    createTokenShowSuccessIndicator.value = false;
    createTokenError.value =
      'Token creation failed, response is missing the token data: ' + res.data;
  }
  creatingToken.value = false;
});

// Handle any errors during token creation.
onCreateTokenError((err) => {
  createTokenError.value = err.message;
  createTokenSuccess.value = false;
  createTokenShowSuccessIndicator.value = false;
  creatingToken.value = false;
});

// Function to handle token creation form submission.
function handleCreateToken() {
  // Clear previous messages
  createTokenError.value = null;
  createTokenSuccess.value = false;
  createTokenShowSuccessIndicator.value = false;
  creatingToken.value = true;

  // Generate random key
  newTokenValue.value = genApiKey();

  // Build input object (adjust field names as needed).
  createApiKey({
    keyName: newTokenName.value,
    keyValue: newTokenValue.value,
    permissions: newTokenPermissions.value,
    description: newTokenDescription.value,
    expiresAt: newTokenExpiration.value
      ? new Date(newTokenExpiration.value)
      : null,
  });
}

function handleDismissToken() {
  createdTokenValue.value = null;
  createdTokenName.value = null;
  emits('apiKeyDismissed');
}

function copyTokenToClipboard(token: string | null) {
  if (!token) {
    console.error('No token to copy');
    return;
  }
  const clip = useClipboard();
  clip.copy(token);
  showCopyTokenTooltip.value = true;

  // Hide the tooltip after 2 seconds
  setTimeout(() => {
    showCopyTokenTooltip.value = false;
  }, 2000);
}

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock("div", null, [
    (!createdTokenValue.value)
      ? (_openBlock(), _createBlock(_unref(BForm), {
          key: 0,
          onSubmit: _withModifiers(handleCreateToken, ["prevent"])
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(BFormGroup), {
              label: "Token Name",
              "label-for": "token-name",
              class: "mt-3"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "token-name",
                  name: "tokenName",
                  modelValue: newTokenName.value,
                  "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((newTokenName).value = $event)),
                  type: "text",
                  required: ""
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "Token Description",
              "label-for": "token-description",
              class: "mt-3"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormTextarea), {
                  id: "token-description",
                  modelValue: newTokenDescription.value,
                  "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((newTokenDescription).value = $event)),
                  type: "text"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "Permissions",
              "label-for": "token-permissions",
              class: "mt-3"
            }, {
              default: _withCtx(() => [
                _withDirectives(_createElementVNode("select", {
                  id: "token-permissions",
                  "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((newTokenPermissions).value = $event)),
                  class: "form-control"
                }, [...(_cache[9] || (_cache[9] = [
                  _createElementVNode("option", { value: "read" }, "Read", -1),
                  _createElementVNode("option", { value: "write" }, "Write", -1)
                ]))], 512), [
                  [_vModelSelect, newTokenPermissions.value]
                ])
              ]),
              _: 1
            }),
            _createVNode(_unref(BFormGroup), {
              label: "Expiration Date (optional)",
              "label-for": "token-expiration",
              class: "mt-3"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(BFormInput), {
                  id: "token-expiration",
                  modelValue: newTokenExpiration.value,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((newTokenExpiration).value = $event)),
                  type: "date"
                }, null, 8, ["modelValue"])
              ]),
              _: 1
            }),
            _createVNode(_unref(BButton), {
              type: "submit",
              variant: "outline-dark",
              class: "mt-3",
              disabled: creatingToken.value
            }, {
              default: _withCtx(() => [
                (creatingToken.value)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_1, [
                      _cache[10] || (_cache[10] = _createTextVNode(" Creating... ", -1)),
                      _createVNode(_unref(BSpinner), {
                        small: "",
                        label: "Creating"
                      })
                    ]))
                  : (createTokenShowSuccessIndicator.value)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_2, [
                        _cache[11] || (_cache[11] = _createTextVNode(" Token created ", -1)),
                        _createVNode(_component_font_awesome_icon, {
                          icon: ['fa', 'check-double'],
                          class: "me-2"
                        })
                      ]))
                    : (_openBlock(), _createElementBlock("span", _hoisted_3, "Create token"))
              ]),
              _: 1
            }, 8, ["disabled"])
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (createdTokenValue.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
          _cache[14] || (_cache[14] = _createElementVNode("p", null, [
            _createElementVNode("strong", null, "Access token created successfully!"),
            _createTextVNode(" Copy the token now as it will not be shown again. ")
          ], -1)),
          _createVNode(_unref(BInputGroup), { prepend: createdTokenName.value }, {
            append: _withCtx(() => [
              _createVNode(_unref(BButton), {
                id: "copy-button-token",
                variant: "outline-dark",
                onClick: _cache[5] || (_cache[5] = ($event: any) => (copyTokenToClipboard(createdTokenValue.value)))
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_font_awesome_icon, { icon: ['fa', 'copy'] })
                ]),
                _: 1
              }),
              (showCopyTokenTooltip.value)
                ? (_openBlock(), _createBlock(_unref(BTooltip), {
                    key: 0,
                    modelValue: showCopyTokenTooltip.value,
                    "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((showCopyTokenTooltip).value = $event)),
                    ref: "tokenTooltip",
                    target: "copy-button-token",
                    placement: "top"
                  }, {
                    default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                      _createTextVNode(" Access token copied ", -1)
                    ]))]),
                    _: 1
                  }, 8, ["modelValue"]))
                : _createCommentVNode("", true),
              _createVNode(_unref(BButton), {
                id: "hide-button-token",
                variant: "outline-dark",
                onClick: handleDismissToken
              }, {
                default: _withCtx(() => [
                  _createVNode(_component_font_awesome_icon, { icon: ['fa', 'xmark'] })
                ]),
                _: 1
              }),
              _createVNode(_unref(BTooltip), {
                ref: "hideTokenTooltip",
                target: "hide-button-token",
                placement: "top"
              }, {
                default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                  _createTextVNode(" Hide access token ", -1)
                ]))]),
                _: 1
              }, 512)
            ]),
            default: _withCtx(() => [
              _createVNode(_unref(BFormInput), {
                id: "created-token-value",
                type: "password",
                modelValue: createdTokenValue.value,
                "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((createdTokenValue).value = $event)),
                readonly: ""
              }, null, 8, ["modelValue"])
            ]),
            _: 1
          }, 8, ["prepend"])
        ]))
      : _createCommentVNode("", true),
    _createVNode(_unref(BAlert), {
      modelValue: createTokenError.value,
      "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((createTokenError).value = $event)),
      variant: "danger",
      dismissible: "",
      onDismissed: _cache[8] || (_cache[8] = ($event: any) => (createTokenError.value = null)),
      class: "mt-3"
    }, {
      default: _withCtx(() => [
        _createTextVNode(_toDisplayString(createTokenError.value), 1)
      ]),
      _: 1
    }, 8, ["modelValue"])
  ]))
}
}

})